/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import merge from 'lodash.merge';
import {
  createSortParams,
  getAccountId,
  getGridColumnState,
  onUpdateGridColumns,
  reInitializeState
} from '@ion/applications/src/helpers';
import {getMembers} from '@ion/applications/src/pages/admin/groups/BulkEditGroupUtils';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  bulkUpdateDevices,
  createDevice,
  deleteAllGroupMembers,
  deleteDevice as destroyDevice,
  deleteDevices as destroyDevices,
  deleteGroupMembers,
  getAccount,
  getAsset,
  getDevice,
  getDeviceByIdSmartSearch,
  getDeviceByPuls,
  getNotification,
  getServiceStatus,
  getVinData,
  loadAllGroups,
  loadDevicesBySmartSearch,
  loadSCIDevicesBySerialNumber,
  loadSentinelInfo,
  sciUpdate,
  sendSCICommand,
  updateBulkDevices,
  updateBulkSCI,
  updateDevice,
  userPermission
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  Asset,
  AuditChange,
  CTCEntity,
  Device,
  Devices,
  DevicesFilters,
  Entity,
  GenericObject,
  IdReport,
  Links,
  SCIDeviceType
} from '@ion/core/src/types/entities';
import {
  LoadDevicesParams,
  LoadGroupsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {
  getHeartBeat,
  getIdReportVin,
  isMDTDevice
} from '@ion/core/src/utils/DeviceUtils';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  isSCIGPSDevice,
  SCIDeviceSettingsForm,
  SCIDeviceSettingsFormField
} from '@ion/dls/src/editors/DeviceEditorUtils';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {FilterChips} from '@ion/dls/src/types/editors';
import {formatErrorMessage} from '@ion/dls/src/utils/ComponentUtils';
import {
  MAX_IDENTIFIER_SEARCH_LIMIT,
  NO_ID_REPORT
} from '@ion/features/src/search/utils';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {importConfirm} from '../../store/actions/importer';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {handleColumnError} from '../../utils/errorMessages';
import {gridDefaultPageSize, onSetGridColumns} from '../../utils/helpers';
import {getGroupMember} from '../admin/groups/state';
import {getCurrentUser} from '../admin/users/useAuthenticatedUser';
import {
  createAncestorList,
  getBatchIds,
  getHeartBeatInfo,
  getICNHeartBeatData,
  getTagValue,
  getUpdatedGroups
} from './helpers';
import {
  createModularSearchQueryString,
  createSortAndSearchQueryParameters,
  getNewSearchCriteria
} from './ModularSearchUtils';
import {SearchFields} from './types';

// Parses a device and returns the id report's vin information.
export const getVin = (device: Device): string => device?.idReport?.vinInfoList?.[0]?.vin || '';

// define a paging instance for all smart search calls from a given context
const paging = new SmartSearchPaging();

// default device smart search value
export const DEFAULT_SMART_SEARCH_QUERY = `(${SearchFields.ID}: (*))`;

// name of reducer
export const NAME                             = 'devices';

export const INITIALIZE_DEVICES               = 'INITIALIZE_DEVICES';
export const RE_INITIALIZE_DEVICES            = 'RE_INITIALIZE_DEVICES';

export const SET_GRID_COLUMNS                 = 'SET_DEVICES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS              = 'UPDATE_DEVICES_GRID_COLUMNS';

export const SET_VIEW_SUB_GROUPS              = 'SET_DEVICES_VIEW_SUB_GROUPS';

export const SET_INHERITABLE_GROUPS           = 'SET_INHERITABLE_GROUPS';

//grid rows per page
export const SET_GRID_PAGE_SIZE               = 'SET_DEVICES_GRID_PAGE_SIZE';

export const DEVICES_GRID_REFRESHED           = 'DEVICES_GRID_REFRESHED';

export const GET_DEVICES_REQUEST              = 'GET_DEVICES_REQUEST';
export const GET_DEVICES_RESPONSE             = 'GET_DEVICES_RESPONSE';
export const GET_DEVICES_ERROR                = 'GET_DEVICES_ERROR';
export const SET_DEVICES_RESPONSE             = 'SET_DEVICES_RESPONSE';

// device add ...
export const ADD_DEVICE                       = 'ADD_DEVICE';
export const ADD_DEVICE_REQUEST               = 'ADD_DEVICE_REQUEST';
export const ADD_DEVICE_RESPONSE              = 'ADD_DEVICE_RESPONSE';
export const ADD_DEVICE_ERROR                 = 'ADD_DEVICE_ERROR';

// bulk device add ...
export const ADD_DEVICES                      = 'ADD_DEVICES';
export const ADD_DEVICES_REQUEST              = 'ADD_DEVICES_REQUEST';
export const ADD_DEVICES_RESPONSE             = 'ADD_DEVICES_RESPONSE';
export const ADD_DEVICES_ERROR                = 'ADD_DEVICES_ERROR';

// device edit ...
export const EDIT_DEVICE                      = 'EDIT_DEVICE';
export const EDIT_DEVICE_REQUEST              = 'EDIT_DEVICE_REQUEST';
export const EDIT_DEVICE_RESPONSE             = 'EDIT_DEVICE_RESPONSE';
export const EDIT_DEVICE_ERROR                = 'EDIT_DEVICE_ERROR';

// bulk device view/edit ...
export const EDIT_DEVICES                     = 'EDIT_DEVICES';
export const EDIT_DEVICES_REQUEST             = 'EDIT_DEVICES_REQUEST';
export const EDIT_DEVICES_RESPONSE            = 'EDIT_DEVICES_RESPONSE';
export const EDIT_DEVICES_ERROR               = 'EDIT_DEVICES_ERROR';

// bulk devices move ...
export const MOVE_DEVICES                     = 'MOVE_DEVICES';
export const MOVE_DEVICES_REQUEST             = 'MOVE_DEVICES_REQUEST';
export const MOVE_DEVICES_RESPONSE            = 'MOVE_DEVICES_RESPONSE';
export const MOVE_DEVICES_ERROR               = 'MOVE_DEVICES_ERROR';
export const SHOW_MOVED_DEVICES               = 'SHOW_MOVED_DEVICES';

// device view ...
export const VIEW_DEVICE                      = 'VIEW_DEVICE';
export const LOAD_DEVICE_DETAIL               = 'LOAD_DEVICE_DETAIL';
export const VIEW_DEVICE_REQUEST              = 'VIEW_DEVICE_REQUEST';
export const VIEW_DEVICE_RESPONSE             = 'VIEW_DEVICE_RESPONSE';
export const VIEW_DEVICE_ERROR                = 'VIEW_DEVICE_ERROR';

export const GET_ASSET                        = 'GET_ASSET';
export const DEVICE_EVENTS_LATEST             = 'DEVICE_EVENTS_LATEST';
export const VIEW_SHIPPING_INFO               = 'VIEW_SHIPPING_INFO';
export const VIEW_MANUFACTURING_INFO          = 'VIEW_MANUFACTURING_INFO';
export const VIEW_IDREPORT_INFO               = 'VIEW_IDREPORT_INFO';
export const VIEW_SERVICES_REQUEST            = 'VIEW_SERVICES_REQUEST';
export const VIEW_SERVICES_INFO               = 'VIEW_SERVICES_INFO';
export const VIEW_SCIDEVICE_INFO              = 'VIEW_SCIDEVICE_INFO';

// device delete ...
export const DELETE_DEVICE                    = 'DELETE_DEVICE';
export const DELETE_DEVICE_REQUEST            = 'DELETE_DEVICE_REQUEST';
export const DELETE_DEVICE_RESPONSE           = 'DELETE_DEVICE_RESPONSE';
export const DELETE_DEVICE_ERROR              = 'DELETE_DEVICE_ERROR';

// bulk device deletes ...
export const DELETE_DEVICES                   = 'DELETE_DEVICES';
export const DELETE_DEVICES_REQUEST           = 'DELETE_DEVICES_REQUEST';
export const DELETE_DEVICES_RESPONSE          = 'DELETE_DEVICES_RESPONSE';
export const DELETE_DEVICES_ERROR             = 'DELETE_DEVICES_ERROR';

// view device / device audit log ...
export const AUDIT_DEVICE                     = 'AUDIT_DEVICE';
export const AUDIT_DEVICE_REQUEST             = 'AUDIT_DEVICE_REQUEST';
export const AUDIT_DEVICE_RESPONSE            = 'AUDIT_DEVICE_RESPONSE';
export const AUDIT_DEVICE_ERROR               = 'AUDIT_DEVICE_ERROR';
export const AUDIT_DEVICE_SHOW_CHANGES_DIALOG = 'AUDIT_DEVICE_SHOW_CHANGES_DIALOG';

// device swap ...
export const SWAP_DEVICE_SHOW_DIALOG          = 'SWAP_DEVICE_SHOW_DIALOG';
export const SWAP_DEVICE_SHOW_CONFIRM_DIALOG  = 'SWAP_DEVICE_SHOW_CONFIRM_DIALOG';

// event view ...
export const VIEW_EVENT                       = 'VIEW_EVENT';

// alert config view ...
export const VIEW_ALERT_CONFIG                = 'VIEW_ALERT_CONFIG';
export const GET_ALERT_CONFIG_REQUEST         = 'GET_ALERT_CONFIG_REQUEST';
export const GET_ALERT_CONFIG_RESPONSE        = 'GET_ALERT_CONFIG_RESPONSE';
export const GET_ALERT_CONFIG_ERROR           = 'GET_ALERT_CONFIG_ERROR';

// device password view ...
export const IMPORT_DEVICE_PASSWORD           = 'IMPORT_DEVICE_PASSWORD';
export const VIEW_DEVICE_PASSWORD             = 'VIEW_DEVICE_PASSWORD';

//device bulk edit group
export const DEVICE_BULK_EDIT_GROUP           = 'DEVICE_BULK_EDIT_GROUP';

//device services
export const MANAGE_SERVICES                  = 'MANAGE_SERVICES';
export const TASK_LIST                        = 'TASK_LIST';
export const TASK_DETAILS                     = 'TASK_DETAILS';

// user bulk import ...
export const IMPORT_DEVICES                   = 'IMPORT_DEVICES';

// devices filter model ...
export const SET_DEVICES_GRID_FILTER_MODEL    = 'SET_DEVICES_GRID_FILTER_MODEL';

//device SCI update
export const UPDATE_SCI_SETTING_REQUEST       = 'UPDATE_SCI_SETTING_REQUEST';
export const UPDATE_SCI_SETTING_RESPONSE      = 'UPDATE_SCI_SETTING_RESPONSE';
export const UPDATE_SCI_SETTING_ERROR         = 'UPDATE_SCI_SETTING_ERROR';
export const CLEAR_SCI_SETTING                = 'CLEAR_SCI_SETTING';

//view device permission and application subscriptions
export const VIEW_DEVICE_PERMISSION           = 'VIEW_DEVICE_PERMISSION';
export const VIEW_DEVICE_SUBSCRIPTION         = 'VIEW_DEVICE_SUBSCRIPTION';

// search queries
export const UPDATE_DEVICES_SEARCH_QUERIES    = 'UPDATE_DEVICES_SEARCH_QUERIES';
export const RESET_DEVICES_SEARCH_QUERIES     = 'RESET_DEVICES_SEARCH_QUERIES';

//bulk sci update
export const BULK_SCI_UPLOAD                  = 'BULK_SCI_UPLOAD';
export const BULK_SCI_UPDATE                  = 'BULK_SCI_UPDATE';
export const BULK_UPDATE_REQUEST              = 'BULK_UPDATE_REQUEST';
export const UPDATE_BULK_SCI_SETTING_REQUEST  = 'UPDATE_BULK_SCI_SETTING_REQUEST';
export const UPDATE_BULK_SCI_SETTING_RESPONSE = 'UPDATE_BULK_SCI_SETTING_RESPONSE';
export const UPDATE_BULK_SCI_SETTING_ERROR    = 'UPDATE_BULK_SCI_SETTING_ERROR';

export const CLEAR_DEVICE_SELECTION             = 'CLEAR_DEVICE_SELECTION';
export const SET_DEVICES_INCLUDE_SUBACCOUNTS    = 'SET_DEVICES_INCLUDE_SUBACCOUNTS';
export const SET_DEVICES_SELECTED               = 'SET_DEVICES_SELECTED';
export const SET_ARE_TOTAL_DEVICE_ROWS_SELECTED = 'SET_ARE_TOTAL_DEVICE_ROWS_SELECTED';
export const SET_TOTAL_DEVICE_ROWS_COUNT        = 'SET_TOTAL_DEVICE_ROWS_COUNT';
export const SET_DEVICE_SEARCH_FILTERS          = 'SET_DEVICE_SEARCH_FILTERS';
export const SET_DEVICE_FILTERS                 = 'SET_DEVICE_FILTERS';
export const SET_DEVICE_GRID_SEARCH             = 'SET_DEVICE_GRID_SEARCH';
export const REFRESH_DEVICE_GRID                = 'REFRESH_DEVICE_GRID';

// view multiple devices
export const SHOW_VIEW_DEVICES                  = 'SHOW_VIEW_DEVICES';
export const SHOW_BULK_DEVICES_GRID             = 'SHOW_BULK_DEVICES_GRID';
export const SET_BULK_DEVICES_SEARCH            = 'SET_BULK_DEVICES_SEARCH';

// multi tab for view devices
export const SET_DEVICES_TABS                   = 'SET_DEVICES_TABS';
export const RESET_DEVICES_TAB                  = 'RESET_DEVICES_TAB';
export const ADD_DEVICES_TAB                    = 'ADD_DEVICES_TAB';
export const UPDATE_DEVICES_TAB                 = 'UPDATE_DEVICES_TAB';
export const REMOVE_DEVICES_TAB                 = 'REMOVE_DEVICES_TAB';
export const SET_SELECTED_TAB                   = 'SET_SELECTED_TAB';

export const SET_SECONDARY_DEVICE               = 'SET_SECONDARY_DEVICE';
export const SET_SENTINEL_TAB                   = 'SET_SENTINEL_TAB';

//pending jobs
export const SHOW_PENDING_JOBS                  = 'SHOW_PENDING_JOBS';

export const PLATFORM_TYPE_RESULTS              = 'PLATFORM_TYPE_RESULTS';

/**
 * initialize Devices
 */
export const initializeDevices = (): PageInitializeAction => {
  return {type: INITIALIZE_DEVICES};
};

export const reInitializeDevices = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_DEVICES};
};

// Updates the modular search queries for Devices
export const updateDevicesSearchQueries = (searchParams: DevicesFilters) => ({
  type : UPDATE_DEVICES_SEARCH_QUERIES, searchParams
});

// Resets the Devices filters state
export const resetDevicesSearchQueries = () => ({
  type : RESET_DEVICES_SEARCH_QUERIES
});

/**
 * Devices Grid Refreshed
 */
export const devicesGridRefreshed = (): PageInitializeAction => {
  return {type: DEVICES_GRID_REFRESHED};
};

export const showPendingJobs = (pendingJobs, show = true) => ({
  type : SHOW_PENDING_JOBS, pendingJobs, show
});

export const getLastData = (data) => data ? data[data.length-1] : [];

/**
 * get Devices by Smart Search
 */
export const getDevicesBySmartSearch = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  globalGroup,
  columnKeys,
  withLocation = false,
  withIdReport = false,
  isHeartBeat = false,
  includeSubGroups = false,
  includeDeviceSettings = false,
  includeAccountFilter = true,
  includeSortParams = false,
  includeParams = false,
  hasInheritableGroups = false,
  viewInheritableGroup = false,
  includeGroups = false,
  usePulse = false,
  isVbuFwEnabled = true,
  showEOLDevices = false,
  hasVehicleProfile = false,
  hasPendingFileHistory = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_DEVICES_REQUEST});
    const {
      devices: {
        search,
        devices: {
          item: gridDevices
        }
      }
    } = getState();

    const lastData = getLastData(gridDevices || []);
    let maxSearch  = false;

    if (search?.esn || search?.serialNumber) {
      if (search?.esn?.split('OR')?.length > MAX_IDENTIFIER_SEARCH_LIMIT ||
        search?.serialNumber?.split('OR').length > MAX_IDENTIFIER_SEARCH_LIMIT) {
        maxSearch = true;
      }
    } if (
      search?.radioFirmware ||
      search?.idReportTimestamp === NO_ID_REPORT ||
      search?.any || search?.mdt || search?.lmu || search?.sentinelAssociation ||
      search?.jpodFirmware || search?.jpodConfig || search?.androidMdtFirmware || search?.vbusDatabase ||
      search?.ble3Firmware || search?.vbu3Firmware
    ) {
      includeSortParams = true;
      includeParams = true;
    }

    const data: LoadDevicesParams = {
      accountId : getAccountId(getState),
      withLocation,
      withIdReport,
      pageId,
      pageSize,
      includeSubGroups,
      includeDeviceSettings,
      includeAccountFilter,
      includeSortParams,
      includeParams,
      includeGroups,
      hasPendingFileHistory,
      usePulse,
      lastData,
      paging
    };

    if (sort && !includeSortParams) {
      data.sortOn = sort;
    } if (includeSortParams && includeParams) {
      data.sortOn = createSortParams({
        item        : sort[0],
        id          : getAccountId(getState),
        queryParams : createSortAndSearchQueryParameters(search, isVbuFwEnabled),
        columnKeys,
        isVbuFwEnabled
      });
    }

    data.queryString = (filter === DEFAULT_SMART_SEARCH_QUERY)
      ? createModularSearchQueryString(search)
      : filter;

    const searchData = getNewSearchCriteria({
      search,
      isVbuFwEnabled,
      account       : data.accountId,
      viewSubGroups : data.includeSubGroups,
      sort          : sort[0] || 'name',
      excludedIds   : [],
      excluded      : false
    });
    const deviceSearch     = maxSearch ? {...data, ...searchData} : data;
    const gridDeviceSearch = (includeSortParams && includeParams) || maxSearch
      ? deviceSearch?.sortOn : deviceSearch?.queryString;
    dispatch(setDeviceGridSearch(gridDeviceSearch));
    try {
      const {devices, totalCount} = await loadDevicesBySmartSearch(deviceSearch);
      const user                  = getCurrentUser(getState);
      const applications          = user?.applications;
      const deviceswithHeartBeat  = [];
      const deviceswithVin        = [];
      let updatedGroups           = null;
      let ancestorsGroups         = null;
      if (isHeartBeat) {
        const heartBeatData    = getICNHeartBeatData(devices, applications);
        const heartBeatPayload = getHeartBeatInfo(heartBeatData, entityId(globalGroup?.href));

        if (heartBeatPayload) {
          const heartBeatInfo = await getHeartBeat(heartBeatPayload, includeSubGroups);

          if (!isEmpty(heartBeatInfo?.results?.devicecheckinmodel?.devicecheckindata)) {
            const deviceHeartBeat = heartBeatInfo?.results?.devicecheckinmodel?.devicecheckindata;
            devices?.forEach(item => {
              const heartBeatResult = deviceHeartBeat.find(device => {
                return isEqual(device.deviceId, item.esn);
              });
              deviceswithHeartBeat.push({...item, heartBeatInfo: heartBeatResult || {}});
            });
          }
        }
      }

      if (hasVehicleProfile) {
        const vinItems = devices?.map((item) => getVin(item))?.filter(e => typeof e === 'string' && e !== '')?.map(i => `vin=${i}`).join('&');
        if (vinItems?.length !== 0) {
          try {
            const path          = 'admin-devices:grid.vehicleProfile';
            const vinInfo       = await getVinData(vinItems);
            const vinTestResult = vinInfo?.data?.response?.results;
            const checkItems    = deviceswithHeartBeat?.length ? deviceswithHeartBeat : devices;
            checkItems?.forEach(item => {
              const vinItem        = getVin(item);
              const vinItemsResult = vinTestResult.find(device => isEqual(vinItem, device?.vin));
              let getVinValue;
              const {isTestVin}    = vinItemsResult || {};
              if (!isUndefined(isTestVin)) {
                getVinValue = isTestVin
                  ? translate(`${path}.test`)
                  : translate(`${path}.production`);
              }
              deviceswithVin.push({...item, vinInfo: getVinValue});
            });
          } catch (err) {
            error(`An Error occurred while retrieving the Vehicle Profiles for the following VINS: ${vinItems}`);
          }
        }
      }

      if (hasInheritableGroups) {
        try {
          const {currentBatch, batches} = getBatchIds(devices);

          if (currentBatch) {
            batches.push(currentBatch?.slice(0, -1));
          } if (batches?.length) {
            try {
              const inheritableGroups = (await Promise.all(batches?.map(async(item) => {
                const data: LoadGroupsParams = {
                  filterOn : {id: item},
                  pageId   : 1,
                  pageSize : 100
                };
                try {
                  const result = await loadAllGroups(data);
                  return result;
                } catch (err) {
                  error(err);
                }
              })))?.flat();

              if (viewInheritableGroup) {
                try {
                  ancestorsGroups = await createAncestorList(entityId(globalGroup?.href));
                } catch (err) {
                  error(err);
                }
              }

              updatedGroups = getUpdatedGroups(devices, inheritableGroups, ancestorsGroups);
            } catch (err) {
              error(err);
            }
          }
        } catch (err) {
          error(err);
        }
      }

      let items = devices;
      if (deviceswithVin?.length) {
        items = deviceswithVin;
      } else if (deviceswithHeartBeat?.length) {
        items = deviceswithHeartBeat;
      } else if (updatedGroups?.length) {
        items = updatedGroups;
      }

      dispatch({type: GET_DEVICES_RESPONSE});
      dispatch({type: SET_DEVICES_RESPONSE, items});

      callbackFn?.({items, totalCount}, applications);
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_DEVICES_ERROR, error: err});
      callbackFn?.({error: err, items: [], totalCount: 0}, []);
      return {items: [], totalCount: 0};
    }
  };
};

/**
 * Invoked when the Bulk Import Devices Dialog is to be displayed or removed.
 */
export const importAdminDevices = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.device));
    dispatch({type: IMPORT_DEVICES, show, result});
  };
};

/**
 * Invoked when the Add Device editor is to be displayed or removed.
 */
export const addAdminDevice = (show = true) => ({
  type : ADD_DEVICE, show
});

export const setDeviceError = (error = null) => {
  return async(dispatch: Function) => {
    dispatch({type: GET_DEVICES_ERROR, error});
  };
};

/**
 * Invoked when the Bulk Add Devices editor is to be displayed or removed.
 */
export const showBulkAddDevices = (show = true) => {
  return (dispatch: Function) => {
    dispatch(importConfirm(Entity.device));
    dispatch({type: ADD_DEVICES, show});
  };
};

/**
 * Invoked when a new Device is to be created, delegating to the createDevice
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addDevice = (device: Device, usePulse = false): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_DEVICE_REQUEST, device});
    try {
      const created = await createDevice(device, usePulse);
      dispatch({type: ADD_DEVICE_RESPONSE, device: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.device)));
    } catch (err) {
      dispatch({type: ADD_DEVICE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.device, err)));
      error(err);
    }
  };
};

/**
 * Invoked when the Edit Device editor is to be displayed or removed.
 */
export const editAdminDevice = (device: Device, show = true) => ({
  type : EDIT_DEVICE, device, show
});

/**
 * creates a hashMap of device settings need to be updated.
 */
const createUpdatedSCIDeviceSettings = (device: Device): SCIDeviceSettingsForm => {
  const SCIDeviceSettings = Object.values(SCIDeviceSettingsFormField);
  const filteredDeviceMap = {};
  SCIDeviceSettings.forEach(setting => {
    const value = device[setting];
    if (!isUndefined(value)) {
      // switch below is for different key name used in ION-2695
      switch (setting) {
        case SCIDeviceSettingsFormField.GPSActivated:
          filteredDeviceMap.disableGPS = value;
          break;
        case SCIDeviceSettingsFormField.iOnTagEnabled:
          filteredDeviceMap.zMicroDetection = value;
          break;
        default:
          filteredDeviceMap[setting] = value;
      };
    };
  });
  return filteredDeviceMap;
};

interface editDeviceProps {
  device: Device;
  usePulse?: boolean;
  callbackFn?: Function;
  groupsToDelete?: Links;
};

const deviceUpdateError = (err: Error, dispatch: Function) => {
  dispatch({type: EDIT_DEVICE_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.device, err)));
  error(err);
};

/**
 * Invoked when an existing Device is to be updated, delegating to the updateDevice
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editDevice = ({
  device,
  callbackFn,
  usePulse = false,
  groupsToDelete = []
}: editDeviceProps): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_DEVICE_REQUEST});
    try {
      const updated = await updateDeviceWithRetry(device, dispatch, usePulse);
      if (updated) {
        if (isSCIGPSDevice(device)) {
          const settingsToUpdate = createUpdatedSCIDeviceSettings(device);
          const isValid = Object.values(settingsToUpdate).length > 0;
          const deviceCommandReq = isValid ? {
            deviceIdentifiers : [{
              identifier : device.serialNumber,
              type       : 'SERIAL_NUMBER'
            }],
            commands : [{
              commandType : 'SciDeviceSettings',
              parameters  : settingsToUpdate
            }]
          } : null;
          deviceCommandReq && (await sendSCICommand(deviceCommandReq));
        }
        if (groupsToDelete.length) {
          await Promise.allSettled(groupsToDelete?.map(async(group) => {
            const members = {
              account : entityId(updated.account),
              devices : {
                value : `${updated.id}`
              }
            };
            const groupMember = getGroupMember(members);
            await deleteGroupMembers(entityId(group), groupMember);
          }));
        }
        callbackFn?.(updated);
        dispatch({type: EDIT_DEVICE_RESPONSE, device: updated});
        dispatch({type: VIEW_DEVICE, device: null, show: false});
        dispatch(toggleUserPrompt(successEditOptions(Entity.device)));
      }
    } catch (err) {
      deviceUpdateError(err, dispatch);
    }
  };
};

export const updateDeviceWithRetry = async(device: Device, dispatch: Function, usePulse = false) => {
  try {
    return await updateDevice(device, usePulse);
  } catch (err) {
    if (requiresRetry([device], [err])) {
      const {updated, error} = await getSingleDevice(entityId(device));
      if (updated) {
        const retryDevice = {
          ...device,
          version : updated.version
        };
        try {
          return await updateDevice(retryDevice, usePulse);
        } catch (error) {
          dispatch(toggleUserPrompt({
            message          : error && isEqual(error?.errorCode, 409) ? error?.errorText : formatErrorMessage(error),
            autoHideDuration : 3000
          }));
        }
      } else {
        dispatch(toggleUserPrompt({
          message          : error ? formatErrorMessage(error): translate('admin-devices:form.move.singleDeviceError'),
          autoHideDuration : 3000
        }));
      }
    } else {
      deviceUpdateError(err, dispatch);
    }
  }
};

export const editDevices = (devices: Device[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_DEVICES_REQUEST});
    try {
      const {results}        = await bulkUpdateDevices(devices);
      const displayResults   = [...results];
      const promises         = [];
      const commandDeviceIds = [];
      for (const device of devices) {
        if (isSCIGPSDevice(device)) {
          const settingsToUpdate = createUpdatedSCIDeviceSettings(device);
          const isValid = !!Object.values(settingsToUpdate).length;
          const deviceCommandReq = isValid ? {
            deviceIdentifiers : [{
              identifier : device.serialNumber,
              type       : 'SERIAL_NUMBER'
            }],
            commands : [{
              commandType : 'SciDeviceSettings',
              parameters  : settingsToUpdate
            }]
          } : null;
          if (deviceCommandReq) {
            commandDeviceIds.push(device.id);
            promises.push(sendSCICommand(deviceCommandReq));
          }
        }
        removeQueries(QueryKey.DEVICE_GET, false);
      }
      if (promises.length) {
        const settingResults = await Promise.allSettled(promises);
        settingResults.forEach(({status}, index) => {
          const deviceId = commandDeviceIds[index];
          if (status === 'rejected') {
            const deviceIndex = displayResults.findIndex(({id}) => id === deviceId);
            deviceIndex > -1 && displayResults.splice(deviceIndex, 1);
          }
        });
      }
      dispatch({type: EDIT_DEVICES_RESPONSE, devices: results});
      dispatch(toggleUserPrompt(
        successEditOptions(
          translate('common:entityLabels.devices').toLowerCase(),
          {results: displayResults, items: devices})
      ));
    } catch (err) {
      dispatch({type: EDIT_DEVICES_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.device, err)));
      error(err);
    }
  };
};

export const editBulkDevices = (devices: Device[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_DEVICES_REQUEST});
    try {
      const {results} = await updateBulkDevices(devices);
      dispatch({type: EDIT_DEVICES_RESPONSE, devices: results});
      dispatch(toggleUserPrompt(
        successEditOptions(
          translate('common:entityLabels.devices').toLowerCase(),
          {results, items: devices})
      ));
    } catch (err) {
      dispatch({type: EDIT_DEVICES_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.device, err)));
      error(err);
    }
  };
};

export const editAdminDevices = (devices: Devices, show = true) => ({
  type : EDIT_DEVICES, devices, show
});

/**
 * Invoked when the View Device editor is to be displayed or removed.
 */
export const viewAdminDevice = (device: Device, show = true) => ({
  type : VIEW_DEVICE, device, show
});

// Loads the selected device into the store.
export const loadDeviceDetail = (device: Device) => ({
  type : LOAD_DEVICE_DETAIL, device
});

/**
 * Invoked when an selected Device is to be displayed, delegating to the getDevice
 * CTC API and dispatching a response of selected device.
 */
export const viewDevice = (device: Device, refreshDevice = true) => {
  const deviceId           = device?.id;
  const deviceEsn          = device?.esn;
  const deviceArchitecture = device?.architecture;
  const assetId            = entityId(device?.asset?.href);
  const _isMDTDevice       = isMDTDevice(device);
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: VIEW_DEVICE_REQUEST, deviceArchitecture});
    dispatch({type: CLEAR_SCI_SETTING});
    try {
      if (refreshDevice && deviceId) {
        const refreshedDevice = await getDeviceByPuls(deviceId);
        dispatch({type: VIEW_DEVICE_RESPONSE, result: refreshedDevice});
        if (assetId) {
          const assetData = await getAsset(assetId);
          dispatch({type: GET_ASSET, assetData});
        }
        const idReportInfo = refreshedDevice?.idReport?.device?.idReport;
        dispatch({type: VIEW_IDREPORT_INFO, idReportInfo});
        if (deviceArchitecture === 'SCI') {
          const accountId = getAccountId(getState);
          const {applications} = await getAccount(accountId);
          const SCIDeviceData = await getDeviceByPuls(deviceId, deviceArchitecture);
          const devicePermission = await userPermission();
          dispatch({type: VIEW_DEVICE_PERMISSION, devicePermission});
          dispatch({type: VIEW_SCIDEVICE_INFO, SCIDeviceData});
          dispatch({type: VIEW_DEVICE_SUBSCRIPTION, applications});
        }
        if (!_isMDTDevice && deviceEsn) {
          dispatch({type: VIEW_SERVICES_REQUEST});
          try {
            const servicesInfo = await getServiceStatus(deviceEsn);
            dispatch({type: VIEW_SERVICES_INFO, servicesInfo});
          } catch (err) {
            dispatch({type: VIEW_SERVICES_INFO, servicesInfo: null});
            error(err);
          }
        }
      }
    } catch (err) {
      dispatch({type: VIEW_DEVICE_ERROR, error: err});
      dispatch(handleColumnError('Entity', 'Device'));
      error(err);
    }
  };
};

/**
 * Invoked when an selected Device is to be displayed, delegating to the getDevice
 * CTC API and dispatching a response of selected device. The difference is that only
 * the device ID is available and so the device must be looked up.
 */
export const viewDeviceById = (deviceId: number, isCtcAdmin = true, isEdit = false) => {
  return async(dispatch: Function): Promise<any> => {
    try {
      if (deviceId) {
        const deviceDetails = await getDevice(deviceId);

        const [deviceByPulse, deviceBySmartSearch] = await Promise.all([
          getDeviceByPuls(deviceId, deviceDetails[0].architecture),
          getDeviceByIdSmartSearch(deviceId)
        ]);

        const device = merge(deviceBySmartSearch, deviceByPulse);

        if (device) {
          if (isEdit) {
            dispatch(editAdminDevice(device));
          } else {
            dispatch(viewDevice(device, isCtcAdmin));
          }
          dispatch(loadDeviceDetail(device));
          dispatch(viewAdminDevice(device));
        }
      }
    } catch (err) {
      error(err);
    }
  };
};

/**
 * Invoked when the Devices editor is to be displayed or removed.
 */
export const auditDevices = (device: Device, show = true) => ({
  type : AUDIT_DEVICE, device, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_DEVICE_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 *
 * Invoked when the Bulk Move device editor is to be displayed or removed.
 */
export const moveAdminDevices = (devices: Device[], show = true) => ({
  type : MOVE_DEVICES, devices, show
});

// Utility to find if retry is to be done
export const requiresRetry = (devices: Devices, errors: CTCEntity[]): boolean => {
  if (devices?.length === 1 && errors?.length) {
    return isEqual(errors[0]?.errorCode, 409);
  }
  return false;
};

/**
 *
 * Invoked when a collection of existing devices are to be updated, delegating to
 * the updateDevices CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const moveDevices = (devices: Device[]) => {
  let failedDevice = false;
  const failedIds  = [];
  const successIds = [];
  let errors       = null;
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MOVE_DEVICES_REQUEST});
    try {
      const membersList = {
        members : getMembers(devices, Entity.device)
      };
      let responses = null;
      if (membersList.members?.length) {
        await deleteAllGroupMembers(membersList);
        responses = await Promise.all(devices.map(async(device) => {
          const {updated, error} = await getSingleDevice(entityId(device));
          if (updated) {
            successIds.push(entityId(device));
            return {
              ...updated,
              account : device.account
            };
          } else {
            errors = error;
            failedDevice = true;
            failedIds.push(entityId(device));
          }
        }));
      }
      if (!failedDevice || successIds.length > 0) {
        const {results, errors} = await bulkMoveDevices(responses || devices);
        let bulkResults = null;
        let bulkErrors  = null;
        if (requiresRetry(devices, errors)) {
          const {updated, error} = await getSingleDevice(entityId(devices[0]));
          if (updated) {
            const retryDevices = [{
              ...updated,
              account : devices[0].account
            }];
            const {results, errors} = await bulkMoveDevices(retryDevices);
            bulkResults = results;
            bulkErrors = errors;
          } else {
            dispatch(toggleUserPrompt({
              message          : error ? formatErrorMessage(error) : translate('admin-devices:form.move.singleDeviceError'),
              autoHideDuration : 3000
            }));
          }
        }
        dispatch({type: MOVE_DEVICES_RESPONSE, devices: bulkResults || results, errors: bulkErrors || errors});
      } else {
        dispatch(toggleUserPrompt({
          message          : !errors ? (translate(`admin-devices:form.move.${failedIds.length ? 'single' : 'multiple' }DeviceError`)) : formatErrorMessage(errors),
          autoHideDuration : 3000
        }));
      }
    } catch (err) {
      dispatch({type: MOVE_DEVICES_ERROR, error: err});
      error(err);
    }
  };
};

// Utility to retrieve device by passing deviceId.
export const getSingleDevice = async(deviceId: number) => {
  try {
    const device = await getDeviceByPuls(deviceId);
    return {updated: device, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

// Utility to bulk update devices and returns the success results and errors from responses.
export const bulkMoveDevices = async(devices: Devices) => {
  try {
    const {results, errors} = await bulkUpdateDevices(devices);
    return {results, errors};
  } catch (err) {
    error(err);
    return {results: [], errors: []};
  }
};

/**
 *
 * Invoked when the Moved devices grid is to be displayed or removed inside dialog.
 */
export const showMovedDevices = (show = true) => ({
  type : SHOW_MOVED_DEVICES, show
});

// invoked when the multiple devices view editor is to be displayed or removed.
export const showBulkViewDevices = (devices: Device[], show = true) => ({
  type : SHOW_VIEW_DEVICES, devices, show
});

// invoked when the view devices grid is to be displayed or removed inside dialog.
export const showBulkViewGrid = (searchText, show = true) => ({
  type : SHOW_BULK_DEVICES_GRID, searchText, show
});

// invoked when the search text entered in the view devices.
export const setBulkViewSearch = (searchText = '') => ({
  type : SET_BULK_DEVICES_SEARCH, searchText
});

// invoked when the multiple devices selected from the grid
export const setDevicesTabs = (tabs: GenericObject[], selectedTab) => ({
  type : SET_DEVICES_TABS, tabs, selectedTab
});

// invokes when the client try to add or update devices tab...
export const updateDevicesTab = (tab: GenericObject, isUpdate = false, prevTab?: GenericObject) => ({
  type : isUpdate ? UPDATE_DEVICES_TAB : ADD_DEVICES_TAB,
  tab,
  prevTab
});

// invokes when the client try to remove report tab...
export const removeDevicesTab = (tab: GenericObject) => ({type: REMOVE_DEVICES_TAB, tab});

// invokes when the client try to select report tab...
export const setSelectedDevicesTab = (tab: GenericObject) => ({type: SET_SELECTED_TAB, tab});

// invokes when the client try to reset report tab...
export const resetDevicesTab = () => ({type: RESET_DEVICES_TAB});

// invoked when the secondary device is clicked from Sentinel Tab
export const setSecondaryDevice = (identifier: string, show: boolean = false) => ({
  type : SET_SECONDARY_DEVICE, identifier, show
});

// invoked when the sentinel association column is clicked.
export const setSentinelTab = (show: boolean = false) => ({
  type : SET_SENTINEL_TAB, show
});

/**
 * Invoked when the Delete Device Dialog is to be displayed or removed.
 */
export const deleteAdminDevice = (device: Device, show = true) => ({
  type : DELETE_DEVICE, device, show
});

/**
 * Invoked when an existing Device is to be deleted, delegating to the destroy Device
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteDevice = (device: Device) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_DEVICE_REQUEST});
    try {
      const deleted = await destroyDevice(device);
      dispatch({type: DELETE_DEVICE_RESPONSE, device: deleted});
    } catch (err) {
      dispatch({type: DELETE_DEVICE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Bulk Delete Admin Devices Dialog is to be displayed or removed.
 */
export const deleteAdminDevices = (devices: Devices, show = true) => ({
  type : DELETE_DEVICES, devices, show
});

/**
 * Invoked when a collection of existing devices are to be deleted, delegating to
 * the destroyDevices CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteDevices = (devices: Devices) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_DEVICES_REQUEST});
    try {
      const {results, errors} = await destroyDevices(devices);
      if (errors.length > 0) {
        dispatch({type: DELETE_DEVICES_ERROR, error: errors});
      };
      dispatch({type: DELETE_DEVICES_RESPONSE, devices: results});
    } catch (err) {
      dispatch({type: DELETE_DEVICES_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Swap Device editor is to be displayed or removed.
 */
export const swapAdminDevice = (device: Device, show = true) => ({
  type : SWAP_DEVICE_SHOW_DIALOG, device, show
});

export const swapAdminDeviceConfirm = (device: Device, show = true) => ({
  type : SWAP_DEVICE_SHOW_CONFIRM_DIALOG, device, show
});

/**
 * Invoked when the view Event Dialog is to be displayed or removed.
 */
export const viewDeviceEvent = (event: GenericObject, show = true) => ({
  type : VIEW_EVENT, event, show
});

/**
 * Invoked when the view Alert Configuration Dialog is to be displayed or removed.
 */
export const viewAlertConfiguration = (alert: GenericObject, show = true) => ({
  type : VIEW_ALERT_CONFIG, alert, show
});

/**
 * Retrieves the Alert Configuration data for the selected alert
 */
export const getAlertConfiguration = (alertConfigId: number) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: GET_ALERT_CONFIG_REQUEST});
    try {
      const result = await getNotification(alertConfigId);
      dispatch({type: GET_ALERT_CONFIG_RESPONSE, alert: result});
    } catch (err) {
      dispatch({type: GET_ALERT_CONFIG_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the import Device Password Dialog is to be displayed or removed.
 */
export const importDevicePassword = (show = true) => ({
  type : IMPORT_DEVICE_PASSWORD, show
});

/**
 * Invoked when the view Device Password Dialog is to be displayed or removed.
 */
export const viewDevicePassword = (deviceIds: string[] | number[], identifierType = '', show = true) => ({
  type : VIEW_DEVICE_PASSWORD, deviceIds, identifierType, show
});

/**
 * Invoked when the Device Bulk Edit Group Dialog is to be displayed or removed.
 */
export const viewBulkEditGroup = (devices: Devices, show = true) => ({
  type : DEVICE_BULK_EDIT_GROUP, devices, show
});

/**
 * Invoked when the Devices Manage Services Dialog is to be displayed or removed.
 */
export const showManageServicesDialog = (devices: Devices, show = true) => ({
  type : MANAGE_SERVICES, devices, show
});

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_DEVICES_GRID_FILTER_MODEL, model
});

/**
 * Invoked when a SCI Setting has been upated for device.
 */
export const updateSCISetting = (changedSettingsFields: GenericObject): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: UPDATE_SCI_SETTING_REQUEST});
    try {
      const {results} = await sciUpdate(changedSettingsFields);
      dispatch({type: UPDATE_SCI_SETTING_RESPONSE, device: results?.data});
      dispatch(toggleUserPrompt({
        message          : translate('admin-devices:view.sciSettings.updateSuccessMessage'),
        autoHideDuration : 3000
      }));
    } catch (err) {
      dispatch({type: UPDATE_SCI_SETTING_ERROR, error: err});
      dispatch(toggleUserPrompt({
        message          : translate('admin-devices:view.sciSettings.updateFailedMessage'),
        autoHideDuration : 2000
      }));
      error(err);
    }
  };
};

/**
 * Invoked when Bulk SCI devices setting import is to be displayed or removed
 */
export const bulkSCIUpload = (show = true) => ({
  type : BULK_SCI_UPLOAD, show
});

/**
 * Invoked when Bulk SCI devices setting Update is to be displayed or removed
 */
export const bulkSCIUpdate = (devices: Devices, show = true) => ({
  type : BULK_SCI_UPDATE, devices, show
});

/**
 * Invoked when a all devices are selected for Bulk SCI devices setting Update.
 */
export const allDevicesBulkSCIUpdate = ({searchCriteria}, deviceType: string[], accountID: number) => {
  const searchQuery = searchCriteria[0]?.smartSearch?.queryString;
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: BULK_UPDATE_REQUEST, show: true});
    try {
      const results = await loadSCIDevicesBySerialNumber([], {deviceType, searchQuery}, accountID);
      if (results.length < 500) {
        const devices = results?.map(({device}) => device)
          .filter((device) => Object.values(SCIDeviceType).includes(device?.typeName));
        dispatch({type: BULK_SCI_UPDATE, devices, show: true});
      } else {
        dispatch({type: BULK_UPDATE_REQUEST, show: false});
        dispatch(toggleUserPrompt({
          message          : translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.maxDevicesSelectedMessage'),
          autoHideDuration : 2000
        }));
      }
    } catch (err) {
      dispatch({type: BULK_UPDATE_REQUEST, show: false});
      dispatch(toggleUserPrompt({
        message          : translate('admin-devices:view.sciSettings.updateFailedMessage'),
        autoHideDuration : 2000
      }));
      error(err);
    }
  };
};

/**
 * Invoked when a Bulk SCI Setting has been upated for device.
 */
export const updateBulkSCISetting = (changedSettingsFields: GenericObject, callbackFn?: Function): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: UPDATE_BULK_SCI_SETTING_REQUEST});
    try {
      const {results} = await updateBulkSCI(changedSettingsFields);
      dispatch({type: UPDATE_BULK_SCI_SETTING_RESPONSE, device: results?.data});
      dispatch(toggleUserPrompt({
        message          : translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.bulkUpdateSuccessMessage'),
        autoHideDuration : 3000
      }));
      callbackFn();
    } catch (err) {
      dispatch({type: UPDATE_BULK_SCI_SETTING_ERROR, error: err});
      dispatch(toggleUserPrompt({
        message          : translate('admin-devices:view.sciSettings.updateFailedMessage'),
        autoHideDuration : 2000
      }));
      error(err);
    }
  };
};

/**
 * Loads and filters Sentinel events for a device, supporting
 * pagination and estimated filtered count.
 *
 * This function returns an async function that:
 *   1. Fetches a page of Sentinel events from the backend using `loadSentinelInfo`, with pagination.
 *   2. Filters the events by device type using the provided `sentinelDeviceType` and `deviceType`.
 *   3. Optionally estimates the total count of filtered events (for the first page)
 *      and sets it via `setFilteredTotalCount`.
 *   4. Calls the provided `callback` with the filtered events and their count.
 *   5. Handles errors by invoking the callback with an error object and resetting the filtered count if needed.
 *
 * **Note:** The estimated total filtered count is only an approximation, based on the
 * ratio of filtered to unfiltered events on the first page.
 * This is necessary because backend filtering is not supported and only one page of data is fetched at a time.
 * 
 * loadSentinelEvents({
 *   tabName: 'current',
 *   callback: ({items, totalRows}) => { ... },
 *   deviceType: 'primary',
 *   currentIdentifier: '12345',
 *   sentinelDeviceType: 'deviceType',
 *   setFilteredTotalCount: setCount,
 *   page: 1,
 *   pageSize: 20
 * })();
 * 
 */
export const loadSentinelEvents = ({
  tabName,
  callback,
  deviceType,
  currentIdentifier,
  sentinelDeviceType,
  setFilteredTotalCount,
  page = 1,
  pageSize = 20
}) => {
  return async(): Promise<any> => {
    try {
      const {events} = await loadSentinelInfo({
        deviceId : currentIdentifier
      });

      const filteredEvents = events?.filter(event => {
        const dynamicPayload = event?.appMessage?.dynamicPayload;
        const eventype       = getTagValue(dynamicPayload, sentinelDeviceType);
        return isEqual(eventype, deviceType);
      });

      const startIndex      = (page - 1) * pageSize;
      const paginatedEvents = filteredEvents?.slice(startIndex, startIndex + pageSize);

      setFilteredTotalCount(filteredEvents?.length);

      callback({items: paginatedEvents, totalCount: filteredEvents?.length});
    } catch (err) {
      callback({
        items      : [],
        totalCount : 0,
        route      : tabName,
        error      : {...err, customMessage: true}
      });
    }
  };
};

export const clearDeviceSelection = () => ({
  type : CLEAR_DEVICE_SELECTION
});

export const setIncludeSubAccounts = (includeSubAccounts: boolean = false) => ({
  type : SET_DEVICES_INCLUDE_SUBACCOUNTS, includeSubAccounts
});

export const setDevicesSelected = (selectedDevices: Device[] = []) => ({
  type : SET_DEVICES_SELECTED, selectedDevices
});

export const setAreTotalDeviceRowsSelected = (areTotalRowsSelected: boolean = false) => ({
  type : SET_ARE_TOTAL_DEVICE_ROWS_SELECTED, areTotalRowsSelected
});

export const setTotalDeviceRowsCount = (totalRowsCount: number = 0) => ({
  type : SET_TOTAL_DEVICE_ROWS_COUNT, totalRowsCount
});

export const setDeviceSearch = (search) => ({
  type : SET_DEVICE_SEARCH_FILTERS, search
});

export const setDeviceGridSearch = (search) => ({
  type : SET_DEVICE_GRID_SEARCH, search
});

export const setDeviceFilters = (filters: FilterChips = []) => ({
  type : SET_DEVICE_FILTERS, filters
});

export const refreshDeviceGrid = () => ({
  type : REFRESH_DEVICE_GRID
});

/**
 * Invoked post success of updating device SCI setting.
 */
export const clearSCI = () => ({
  type : CLEAR_SCI_SETTING
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setDevicesViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user click on Inheritable toggle button.
 */
export const setInheritableGroups = (inheritableGroups: boolean = false) => ({
  type : SET_INHERITABLE_GROUPS, inheritableGroups
});

/**
 * Invoked whenever user change the Grid row per page dropdown
 */
export const setDevicesGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

const updateSearch = (params) => {
  const search = {};
  Object.values(SearchFields).map((each) => {
    search[each] = params[each] || null;
  });
  return search;
};

/**
 * REDUCERS
 */

export interface DevicesAction extends AdminAction {
  device?: Device;
  devices?: Device[];
  errors?: ServiceError[];
  includeSubAccounts?: boolean;
  selectedDevices?: Device[];
  areTotalRowsSelected?: boolean;
  totalRowsCount?: number;
  search?: [];
  filters?: FilterChips;
};

export interface DevicesEditAction extends AdminAction {
  device?: Device;
  devices?: Device[];
};

export interface DevicesActionState extends AsyncState {
  selected: Device | Device[] | null;
  result: Device | Device[] | null;
  record?: Device | Device[] | null;
  isEditing: boolean;
  searchParams?: DevicesFilters;
  viewMovedDevices?: boolean;
  errors?: ServiceError[];
};

export interface DevicesAuditState extends DevicesActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}

export interface DevicesSwapState extends DevicesActionState {
  showConfirmDialog?: boolean;
}

export interface DevicesPasswordState extends DevicesActionState {
  showImportDialog?: boolean;
  identifierType?: string;
  selectedIdentifiers?: string[] | number[];
}

interface DevicesImportState {
  isImporting: boolean;
  result: Device | Device[] | null;
};

export interface DevicesViewActionState extends DevicesActionState {
  assetData?: Asset;
  latestIdReport?: any;
  SCIDeviceData?: any;
  crashboxStatus?: any;
  shippingInfo?: any;
  manufacturingInfo?: any;
  idReportInfo?: IdReport;
  servicesInfo?: any;
  servicesLoading?: boolean;
  isSCIDevice?: boolean;
}

export interface Permission {
  forEach: any;
  action: string;
  additionalPermissions: Permission | Permission[] | any;
  description: string;
  groups: any;
  id: number;
  resource: string;
};

export interface DevicePermissions {
  permissions: Permission | Permission[] | any,
  applications: Links
};

export interface DeviceHeartBeatState {
  items: Device | Device[];
};

export interface BulkViewDevicesState {
  selected: Device | Device[] | null;
  showViewDialog: boolean;
  showGridDialog: boolean;
  searchText: string;
  tabs: Device[] | null;
  selectedTab: Device | null;
};

export interface DevicesState extends PageState {
  devices: {
    error: ServiceError;
    loading: boolean;
    item: Device | Device[] | null;
  },
  pendingJobs: {
    error: ServiceError;
    loading: boolean;
  },
  showPendingJobs: boolean;
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    inheritableGroups: boolean,
    filterModel: FilterModel | {},
    selectedDevices: Device[],
    areTotalRowsSelected: boolean,
    totalRowsCount: number,
    pageSize: number,
    search: any,
    gridSearch: any,
    filters: FilterChips,
    refresh: boolean
  },
  deviceDetail: Device,
  add: DevicesActionState,
  edit: DevicesActionState,
  view: DevicesViewActionState,
  destroy: DevicesActionState,
  audit: DevicesAuditState,
  bulkAdd: DevicesActionState & DevicesImportState,
  bulkEdit: DevicesActionState,
  bulkDelete: DevicesActionState,
  bulkImport: DevicesImportState,
  viewEvent: DevicesActionState,
  viewAlertConfig: DevicesActionState,
  move: DevicesActionState,
  devicePassword: DevicesPasswordState,
  bulkEditGroup: DevicesActionState,
  manageServices: DevicesActionState,
  updateSCI: {
    error: ServiceError;
    loading: boolean;
    result: Device | Device[] | null;
  },
  devicePermissions: DevicePermissions,
  search: DevicesFilters,
  swap: DevicesSwapState,
  bulkSCIUpdate: {
    bulkUpload: boolean,
    bulkUpdate: boolean,
    selected: Device | Device[] | null,
    loading: boolean,
    error: ServiceError,
    dialogLoading: boolean
  },
  sentinelAssociation: {
    secondaryIdentifier: string,
    showSecondaryTab: boolean,
    showSentinelTab: boolean
  },
  includeSubAccounts: boolean,
  bulkViewDevices: BulkViewDevicesState
};

// defines the user preference key for the devices page ...
export const PREFERENCE = 'devices';

const clearSearch = () => ({
  any               : null,
  id                : null,
  name              : null,
  airId             : null,
  esn               : null,
  iccid             : null,
  imei              : null,
  imei2             : null,
  meid              : null,
  serialNumber      : null,
  appId             : null,
  typeId            : null,
  category          : null,
  firmwareVersion   : null,
  configVersion     : null,
  idReportTimestamp : null,
  lastReportedTime  : null,
  status            : null,
  groupIds          : null
});

// defines the initial state for the reducer ...
export const initialState: DevicesState = {
  isInitialized : false,
  deviceDetail  : null,
  devices       : {
    error   : null,
    loading : false,
    item    : null
  },
  pendingJobs : {
    error   : null,
    loading : false
  },
  showPendingJobs : false,
  grid            : {
    columnState          : null,
    preferedColumnState  : null,
    hasUpdate            : false,
    includeSubGroups     : false,
    inheritableGroups    : false,
    filterModel          : {},
    selectedDevices      : [],
    areTotalRowsSelected : false,
    totalRowsCount       : 0,
    pageSize             : gridDefaultPageSize,
    search               : null,
    gridSearch           : null,
    filters              : [],
    refresh              : false
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  view : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    assetData         : null,
    latestIdReport    : null,
    SCIDeviceData     : null,
    crashboxStatus    : null,
    shippingInfo      : null,
    manufacturingInfo : null,
    idReportInfo      : undefined,
    servicesInfo      : null,
    servicesLoading   : false,
    isSCIDevice       : false
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  bulkAdd : {
    isEditing   : false,
    selected    : null,
    loading     : false,
    error       : null,
    isImporting : false,
    result      : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  move : {
    isEditing        : false,
    selected         : null,
    loading          : false,
    error            : null,
    result           : null,
    errors           : null,
    viewMovedDevices : false
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  viewEvent : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  viewAlertConfig : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  devicePassword : {
    isEditing           : false,
    selected            : null,
    loading             : false,
    error               : null,
    result              : null,
    showImportDialog    : false,
    identifierType      : '',
    selectedIdentifiers : null
  },
  bulkEditGroup : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  manageServices : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  updateSCI : {
    loading : false,
    error   : null,
    result  : null
  },
  devicePermissions : {
    permissions  : null,
    applications : null
  },
  swap : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showConfirmDialog : false
  },
  bulkSCIUpdate : {
    bulkUpload    : false,
    bulkUpdate    : false,
    selected      : null,
    loading       : false,
    error         : null,
    dialogLoading : null
  },
  sentinelAssociation : {
    secondaryIdentifier : null,
    showSecondaryTab    : false,
    showSentinelTab     : false
  },
  search             : clearSearch(),
  includeSubAccounts : false,
  bulkViewDevices    : {
    selected       : null,
    showViewDialog : false,
    showGridDialog : false,
    searchText     : '',
    tabs           : [],
    selectedTab    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DEVICES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.grid.selectedDevices     = [];
    draft.search                   = clearSearch();
    draft.destroy.error = draft.destroy.selected = null;
    draft.bulkDelete.error = draft.bulkDelete.record = null;
  },
  [RE_INITIALIZE_DEVICES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, devices}: DevicesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devices.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_DEVICES_GRID_FILTER_MODEL] : ({grid}: DevicesState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: DevicesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_DEVICES_REQUEST] : ({devices}: DevicesState) => {
    devices.loading = true;
  },
  [GET_DEVICES_RESPONSE] : ({devices}: DevicesState) => {
    devices.loading = false;
  },
  [GET_DEVICES_ERROR] : ({devices}: DevicesState, {error}: DevicesAction) => {
    devices.loading = false;
    devices.error   = error;
  },
  [SET_DEVICES_RESPONSE] : ({devices}: DevicesState, {items}: DeviceHeartBeatState) => {
    devices.item = items;
  },
  [ADD_DEVICE] : ({add}: DevicesState, {show}: DevicesAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_DEVICE_REQUEST] : ({add}: DevicesState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_DEVICE_RESPONSE] : ({add}: DevicesState, {device}: DevicesAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = device;
  },
  [ADD_DEVICE_ERROR] : ({add}: DevicesState, {error}: DevicesAction) => {
    add.loading = false;
    add.error = error;
  },
  [ADD_DEVICES] : ({bulkAdd}: DevicesState, {show}: DevicesAction) => {
    bulkAdd.isEditing = show;
    bulkAdd.result = null;
  },
  [ADD_DEVICES_REQUEST] : ({bulkAdd}: DevicesState) => {
    bulkAdd.loading = true;
    bulkAdd.error = bulkAdd.result = null;
  },
  [ADD_DEVICES_RESPONSE] : ({bulkAdd}: DevicesState, {device}: DevicesAction) => {
    bulkAdd.loading = false;
    bulkAdd.isEditing = false;
    bulkAdd.result = device;
  },
  [ADD_DEVICES_ERROR] : ({bulkAdd}: DevicesState, {error}: DevicesAction) => {
    bulkAdd.loading = false;
    bulkAdd.error = error;
  },
  [EDIT_DEVICE] : ({edit}: DevicesState, {device, show}: DevicesEditAction) => {
    edit.selected = show ? device : null;
    edit.isEditing = show;
    edit.result = null;
  },
  [EDIT_DEVICE_REQUEST] : ({edit}: DevicesState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_DEVICE_RESPONSE] : ({edit}: DevicesState, {device}: DevicesAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = device;
  },
  [EDIT_DEVICE_ERROR] : ({edit}: DevicesState, {error}: DevicesAction) => {
    edit.loading = false;
    edit.error = error;
  },
  [EDIT_DEVICES] : ({bulkEdit}: DevicesState, {devices, show}: DevicesEditAction) => {
    bulkEdit.selected = show ? devices : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_DEVICES_REQUEST] : ({bulkEdit}: DevicesState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_DEVICES_RESPONSE] : ({bulkEdit}: DevicesState, {devices}: DevicesAction) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = devices;
  },
  [EDIT_DEVICES_ERROR] : ({bulkEdit}: DevicesState, {error}: DevicesAction) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },
  [VIEW_DEVICE] : ({view}: DevicesState, {show, device}: DevicesAction) => {
    view.selected  = show ? device : null;
    view.isEditing = show;
    view.result    = null;
  },
  [LOAD_DEVICE_DETAIL] : (devices: DevicesState, {device}: DevicesAction) => {
    devices.deviceDetail = device;
  },
  [VIEW_DEVICE_REQUEST] : ({view}: DevicesState, {deviceArchitecture}) => {
    view.loading           = true;
    view.error             = view.result = null;
    view.assetData         = null;
    view.latestIdReport    = null;
    view.SCIDeviceData     = null;
    view.crashboxStatus    = null;
    view.shippingInfo      = null;
    view.manufacturingInfo = null;
    view.idReportInfo      = undefined;
    view.servicesInfo      = null;
    view.servicesLoading   = false;
    view.isSCIDevice       = deviceArchitecture === 'SCI';
  },
  [VIEW_DEVICE_RESPONSE] : ({view}: DevicesState, {result}: DevicesAction) => {
    view.loading = false;
    view.result  = result;
  },
  [VIEW_DEVICE_ERROR] : ({view}: DevicesState, {error}: DevicesAction) => {
    view.loading = false;
    view.servicesLoading = false;
    view.error   = error;
  },
  [VIEW_SERVICES_REQUEST] : ({view}: DevicesState) => {
    view.servicesLoading = true;
  },
  [VIEW_SERVICES_INFO] : ({view}: DevicesState, {servicesInfo}) => {
    view.servicesLoading = false;
    view.servicesInfo  = servicesInfo;
  },
  [GET_ASSET] : ({view}: DevicesState, {assetData}) => {
    view.assetData = assetData;
  },
  [DEVICE_EVENTS_LATEST] : ({view}: DevicesState, {latestIdReport}) => {
    view.latestIdReport = latestIdReport;
  },
  [VIEW_SHIPPING_INFO] : ({view}: DevicesState, {shippingInfo}) => {
    view.shippingInfo  = shippingInfo;
  },
  [VIEW_MANUFACTURING_INFO] : ({view}: DevicesState, {manufacturingInfo}) => {
    view.manufacturingInfo = manufacturingInfo;
  },
  [VIEW_IDREPORT_INFO] : ({view}: DevicesState, {idReportInfo}) => {
    view.idReportInfo = idReportInfo;
  },
  [VIEW_SCIDEVICE_INFO] : ({view}: DevicesState, {SCIDeviceData}) => {
    view.SCIDeviceData = SCIDeviceData;
  },
  [MOVE_DEVICES] : ({move}: DevicesState, {devices, show}: DevicesAction) => {
    move.selected = show ? devices : null;
    move.isEditing = show;
    move.result = null;
  },
  [MOVE_DEVICES_REQUEST] : ({move}: DevicesState) => {
    move.loading = true;
    move.error = move.result = null;
  },
  [MOVE_DEVICES_RESPONSE] : ({move}: DevicesState, {devices, errors}: DevicesAction) => {
    move.loading = false;
    move.isEditing = false;
    move.result = devices;
    move.errors = errors;
  },
  [MOVE_DEVICES_ERROR] : ({move}: DevicesState, {error}: DevicesAction) => {
    move.loading = false;
    move.error = error;
  },
  [SHOW_MOVED_DEVICES] : ({move}: DevicesState, {show}: DevicesAction) => {
    move.viewMovedDevices = show;
  },
  [DELETE_DEVICE] : ({destroy}: DevicesState, {device, show}: DevicesAction) => {
    destroy.selected = show ? device : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_DEVICE_REQUEST] : ({destroy}: DevicesState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_DEVICE_RESPONSE] : ({destroy}: DevicesState, {device}: DevicesAction) => {
    destroy.result = device;
    destroy.selected = null;
    destroy.loading = destroy.isEditing = false;
  },
  [DELETE_DEVICE_ERROR] : ({destroy}: DevicesState, {error}: DevicesAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.isEditing = false;
  },
  [DELETE_DEVICES] : (
    {bulkDelete}: DevicesState, {devices, show}: DevicesAction
  ) => {
    bulkDelete.selected = show ? devices : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_DEVICES_REQUEST] : ({bulkDelete}: DevicesState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_DEVICES_RESPONSE] : (
    {bulkDelete}: DevicesState, {devices}: DevicesAction
  ) => {
    bulkDelete.loading = bulkDelete.isEditing = false;
    bulkDelete.selected = null;
    bulkDelete.result = bulkDelete.record = devices;
  },
  [DELETE_DEVICES_ERROR] : (
    {bulkDelete}: DevicesState, {error}: DevicesAction
  ) => {
    bulkDelete.error = error;
    bulkDelete.result = bulkDelete.record = null;
    bulkDelete.loading = bulkDelete.isEditing = false;
  },
  [AUDIT_DEVICE] : ({audit}: DevicesState, {device, show}: DevicesAction) => {
    audit.selected = show ? device : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_DEVICE_REQUEST] : ({audit}: DevicesState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_DEVICE_RESPONSE] : ({audit}: DevicesState, {result}: DevicesAction) => {
    audit.loading = false;
    audit.isEditing = false;
    audit.result = result;
  },
  [AUDIT_DEVICE_ERROR] : ({audit}: DevicesState, {error}: DevicesAction) => {
    audit.loading = false;
    audit.error = error;
  },
  [AUDIT_DEVICE_SHOW_CHANGES_DIALOG] : ({audit}: DevicesState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [VIEW_EVENT] : ({viewEvent}: DevicesState, {event, show}) => {
    viewEvent.selected = show ? event : null;
    viewEvent.isEditing = show;
  },
  [VIEW_ALERT_CONFIG] : ({viewAlertConfig}, {alert, show}) => {
    viewAlertConfig.selected = show ? alert : null;
    viewAlertConfig.isEditing = show;
  },
  [GET_ALERT_CONFIG_REQUEST] : ({viewAlertConfig}) => {
    viewAlertConfig.loading = true;
    viewAlertConfig.error = viewAlertConfig.result = null;
  },
  [GET_ALERT_CONFIG_RESPONSE] : ({viewAlertConfig}, {alert}) => {
    viewAlertConfig.loading = false;
    viewAlertConfig.result = alert;
  },
  [GET_ALERT_CONFIG_ERROR] : ({viewAlertConfig}, {error}: DevicesAction) => {
    viewAlertConfig.loading = false;
    viewAlertConfig.error = error;
  },
  [IMPORT_DEVICE_PASSWORD] : ({devicePassword}, {show}) => {
    devicePassword.showImportDialog = show;
    devicePassword.isEditing = false;
  },
  [VIEW_DEVICE_PASSWORD] : ({devicePassword}, {deviceIds, identifierType, show}) => {
    devicePassword.selectedIdentifiers = show ? deviceIds : null;
    devicePassword.identifierType = show ? identifierType : '';
    devicePassword.isEditing = show;
  },
  [DEVICE_BULK_EDIT_GROUP] : ({bulkEditGroup}: DevicesState, {devices, show}: DevicesEditAction) => {
    bulkEditGroup.selected = show ? devices : null;
    bulkEditGroup.isEditing = show;
    bulkEditGroup.result = null;
  },
  [MANAGE_SERVICES] : ({manageServices}: DevicesState, {devices, show}: DevicesAction) => {
    manageServices.selected = show ? devices : null;
    manageServices.isEditing = show;
  },
  [DEVICES_GRID_REFRESHED] : (
    {add, edit, audit, destroy, bulkEdit, bulkDelete, bulkImport, bulkSCIUpdate, grid}: DevicesState
  ) => {
    grid.refresh = false;
    add.result = null;
    edit.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
    bulkImport.result = null;
    bulkSCIUpdate.bulkUpdate = null;
    bulkSCIUpdate.bulkUpload = null;
  },
  [UPDATE_SCI_SETTING_REQUEST] : ({updateSCI}: DevicesState) => {
    updateSCI.loading = true;
  },
  [UPDATE_SCI_SETTING_RESPONSE] : ({updateSCI}: DevicesState, {device}) => {
    updateSCI.loading = false;
    updateSCI.result = device?.response;
  },
  [UPDATE_SCI_SETTING_ERROR] : ({updateSCI}: DevicesState, {error}: DevicesAction) => {
    updateSCI.loading = false;
    updateSCI.error = error;
  },
  [CLEAR_SCI_SETTING] : ({updateSCI}: DevicesState) => {
    updateSCI.loading = false;
    updateSCI.result = null;
    updateSCI.error = null;
  },
  [BULK_SCI_UPLOAD] : ({bulkSCIUpdate}, {show}) => {
    bulkSCIUpdate.bulkUpdate = false;
    bulkSCIUpdate.bulkUpload = show;
    bulkSCIUpdate.dialogLoading = false;
  },
  [BULK_SCI_UPDATE] : ({bulkSCIUpdate}, {devices, show}) => {
    bulkSCIUpdate.selected = show ? devices : null;
    bulkSCIUpdate.bulkUpdate = show;
    bulkSCIUpdate.bulkUpload = false;
    bulkSCIUpdate.dialogLoading = false;
  },
  [VIEW_DEVICE_PERMISSION] : ({devicePermissions}: DevicesState, devicePermission) => {
    devicePermissions.permissions = devicePermission?.devicePermission;
  },
  [VIEW_DEVICE_SUBSCRIPTION] : ({devicePermissions}: DevicesState, applications) => {
    devicePermissions.applications = applications?.applications;
  },
  [IMPORT_DEVICES] : ({bulkImport}: DevicesState, {show, result}: DevicesAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [SWAP_DEVICE_SHOW_DIALOG] : ({swap}: DevicesState, {device, show}: DevicesEditAction) => {
    swap.selected = show ? device : null;
    swap.showConfirmDialog = false;
    swap.isEditing = show;
    swap.result = null;
  },
  [SWAP_DEVICE_SHOW_CONFIRM_DIALOG] : ({swap}: DevicesState, {device, show}) => {
    swap.showConfirmDialog = show;
    swap.selected = device;
  },
  [UPDATE_DEVICES_SEARCH_QUERIES] : (draft: DevicesState, {searchParams}) => {
    draft.search = updateSearch(searchParams);
  },
  [RESET_DEVICES_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  },
  [UPDATE_BULK_SCI_SETTING_REQUEST] : ({bulkSCIUpdate}, {show}) => {
    bulkSCIUpdate.loading = true;
  },
  [UPDATE_BULK_SCI_SETTING_RESPONSE] : ({bulkSCIUpdate}, {device}) => {
    bulkSCIUpdate.loading = false;
    bulkSCIUpdate.bulkUpdate = false;
    bulkSCIUpdate.bulkUpload = false;
    bulkSCIUpdate.dialogLoading = false;
  },
  [UPDATE_BULK_SCI_SETTING_ERROR] : ({bulkSCIUpdate}: DevicesState, {error}: DevicesAction) => {
    bulkSCIUpdate.loading = false;
    bulkSCIUpdate.dialogLoading = false;
    bulkSCIUpdate.error = error;
  },
  [BULK_UPDATE_REQUEST] : ({bulkSCIUpdate}, {show}) => {
    bulkSCIUpdate.dialogLoading = show;
  },
  [CLEAR_DEVICE_SELECTION] : (draft: DevicesState) => {
    draft.includeSubAccounts = false;
    draft.grid.selectedDevices = [];
    draft.grid.areTotalRowsSelected = false;
    draft.grid.totalRowsCount = 0;
    draft.grid.search = null;
    draft.grid.filters = [];
  },
  [SET_DEVICES_INCLUDE_SUBACCOUNTS] : (draft: DevicesState, {includeSubAccounts}: DevicesAction) => {
    draft.includeSubAccounts = includeSubAccounts;
  },
  [SET_DEVICES_SELECTED] : ({grid}: DevicesState, {selectedDevices}: DevicesAction) => {
    grid.selectedDevices = selectedDevices;
  },
  [SET_ARE_TOTAL_DEVICE_ROWS_SELECTED] : ({grid}: DevicesState, {areTotalRowsSelected}: DevicesAction) => {
    grid.areTotalRowsSelected = areTotalRowsSelected;
  },
  [SET_TOTAL_DEVICE_ROWS_COUNT] : ({grid}: DevicesState, {totalRowsCount}: DevicesAction) => {
    grid.totalRowsCount = totalRowsCount;
  },
  [SET_DEVICE_SEARCH_FILTERS] : ({grid}: DevicesState, {search}: DevicesAction) => {
    grid.search = search;
  },
  [SET_DEVICE_GRID_SEARCH] : ({grid}: DevicesState, {search}: DevicesAction) => {
    grid.gridSearch = search;
  },
  [SET_DEVICE_FILTERS] : ({grid}: DevicesState, {filters}: DevicesAction) => {
    grid.filters = filters;
  },
  [REFRESH_DEVICE_GRID] : ({grid}: DevicesState) => {
    grid.refresh = true;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: DevicesState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_INHERITABLE_GROUPS] : ({grid}: DevicesState, {inheritableGroups}) => {
    grid.inheritableGroups = inheritableGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: DevicesState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [SHOW_VIEW_DEVICES] : ({bulkViewDevices}: DevicesState, {devices, show}: DevicesAction) => {
    bulkViewDevices.selected = show ? devices : null;
    bulkViewDevices.showViewDialog = show;
  },
  [SHOW_BULK_DEVICES_GRID] : ({bulkViewDevices}: DevicesState, {searchText, show}) => {
    bulkViewDevices.searchText = show ? searchText : '';
    bulkViewDevices.showGridDialog = show;
  },
  [SET_BULK_DEVICES_SEARCH] : ({bulkViewDevices}: DevicesState, {searchText}) => {
    bulkViewDevices.searchText = searchText;
  },
  [SET_DEVICES_TABS] : ({bulkViewDevices}: DevicesState, {tabs, selectedTab}) => {
    bulkViewDevices.tabs = tabs;
    bulkViewDevices.selectedTab = selectedTab;
  },
  [RESET_DEVICES_TAB] : ({bulkViewDevices}: DevicesState) => {
    bulkViewDevices.tabs = [];
    bulkViewDevices.selectedTab = null;
  },
  [SET_SECONDARY_DEVICE] : ({sentinelAssociation}: DevicesState, {identifier, show}) => {
    sentinelAssociation.secondaryIdentifier = identifier;
    sentinelAssociation.showSecondaryTab = show;
  },
  [SET_SENTINEL_TAB] : ({sentinelAssociation}: DevicesState, {show}) => {
    sentinelAssociation.showSentinelTab = show;
  },
  [ADD_DEVICES_TAB] : ({bulkViewDevices}: DevicesState, {tab}) => {
    const targetId = tab?.data?.id;
    if (targetId) {
      const existing = bulkViewDevices.tabs?.find(t => t?.data?.id === targetId);
      if (existing) {
        bulkViewDevices.selectedTab = existing;
      } else {
        bulkViewDevices.tabs.push(tab);
        bulkViewDevices.selectedTab = tab;
      }
    }
  },
  [UPDATE_DEVICES_TAB] : ({bulkViewDevices}: DevicesState, {tab, prevTab}) => {
    bulkViewDevices.tabs = bulkViewDevices.tabs?.map((current) => {
      if (current?.data.id === prevTab?.id) {
        return tab;
      }
      return current;
    });
  },
  [SET_SELECTED_TAB] : ({bulkViewDevices}: DevicesState, {tab}) => {
    bulkViewDevices.selectedTab = tab;
  },
  [REMOVE_DEVICES_TAB] : ({bulkViewDevices}: DevicesState, {tab}) => {
    bulkViewDevices.tabs = bulkViewDevices.tabs?.filter(current => current?.data.id !== tab?.id);
  },
  [SHOW_PENDING_JOBS] : (draft: DevicesState, {pendingJobs, show}) => {
    draft.showPendingJobs = show;
    draft.pendingJobs     = pendingJobs;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: DevicesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
